import { useState, useEffect, useMemo } from 'react';

import { Text, Button, LineChart } from '@ui-component-library';
import Skeleton from "../../comps/Skeleton";
import Retry from "../../comps/RetryBlock";
import Tooltip from "./Tooltip";

import { useWoodprices } from "../context/Woodprices";
import { usePlusBlockerDialog } from "@components/PlusBlockerDialog/PlusBlockerDialog.context";
import TrendUp from '@assets/icons/trend-up.svg';
import TrendDown from '@assets/icons/trend-down.svg';
import LockIcon from '@assets/icons/lock.svg';
import PlusIcon from '@assets/icons/premium-plus.svg';
import ArrowIcon from '@assets/icons/arrow-right.svg';

const dummyData = {
  change: {
    "3m": { sawtimber: 2.5, pulpwood: 2.5, total: 2.5 },
    "6m": { sawtimber: 2.5, pulpwood: 2.5, total: 2.5 },
    "y": { sawtimber: 2.5, pulpwood: 2.5, total: 2.5 },
    "1y": { sawtimber: 2.5, pulpwood: 2.5, total: 2.5 },
    "3y": { sawtimber: 2.5, pulpwood: 2.5, total: 2.5 },
    "max": { sawtimber: 2.5, pulpwood: 2.5, total: 2.5 }
  },
  chart: [
    {
      label: "sep. 25",
      date: "2025-09",
      sawtimber: 755,
      pulpwood: 650,
      all: [{ value: "sawtimber", price: 755, label: "Sågtimmer" }, { value: "pulpwood", price: 650, label: "Massaved" }]
    },
    {
      label: "okt. 25",
      date: "2025-10",
      sawtimber: 730,
      pulpwood: 725,
      all: [{ value: "sawtimber", price: 730, label: "Sågtimmer" }, { value: "pulpwood", price: 725, label: "Massaved" }]
    },
    {
      label: "nov. 25",
      date: "2025-11",
      sawtimber: 952,
      pulpwood: 680,
      all: [{ value: "sawtimber", price: 952, label: "Sågtimmer" }, { value: "pulpwood", price: 680, label: "Massaved" }]
    }
  ]
}

const TopBlock = ({ title, value, trend = true }) => {
  if (typeof value === 'number') {
    trend = value > 0
  }
  const color = trend ? '#257B57' : '#B91C1C'
  const textColor = trend ? 'text-[#257B57]' : 'text-[#B91C1C]'
  const icon = trend ? <TrendUp fill={color} width='16px' height='16px' /> : <TrendDown fill={color} width='16px' height='16px' />
  return <div className='min-w-[160px] w-full rounded-xl bg-[#F5F5F5] p-6'>
    <Text as='p' className='text-[#737373] line-clamp-1' variant='text-xs/leading-normal/normal'>{title}</Text>
    <div className='flex items-center gap-1'>
      {icon}
      <Text className={textColor} variant='text-base/leading-normal/medium'>{trend ? '+' : ''}{value.toLocaleString('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}{typeof value === 'number' ? '%' : ''}</Text>
    </div>
  </div>
}

const BottomValue = ({ value }) => {
  const trend = value > 0
  const color = trend ? 'text-[#257B57]' : 'text-[#C63A35]'
  return <Text className={color} variant='text-xs/leading-none/normal'>{trend ? '+' : ''}{value.toLocaleString('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</Text>
}

export default function HistoricPrices({ selectMuni }) {
  const { openPlusBlockerDialog } = usePlusBlockerDialog();
  const { historicData, historicOptions, setHistoricOptions, muni, fetchHistoricPrices } = useWoodprices();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const timeframeOptions = [{ label: '3 m', value: '3m' }, { label: '6 m', value: '6m' }, { label: 'i år', value: 'y' }, { label: '1 år', value: '1y' }, { label: '3 år', value: '3y' }]

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const data = useMemo(() => {
    if (historicData == 'not_plus_user') {
      return dummyData;
    }

    if (!historicData || historicData.length === 0) {
      return {
        timeframeFiltered: Object.fromEntries(timeframeOptions.map(o => [o.value, []])),
        change: Object.fromEntries(timeframeOptions.map(o => [o.value, {}]))
      };
    }

    let timeframeFiltered = Object.fromEntries(timeframeOptions.map(o => [o.value, []]))

    // Creating 5 year data array
    timeframeFiltered['max'] = historicData;

    // Creating 5 year data array
    timeframeFiltered['3y'] = historicData.slice(historicData.length - 36);

    // Creating this year data array
    const currentYear = new Date().getFullYear();
    timeframeFiltered['y'] = historicData.filter(item => {
      return item.date.startsWith(currentYear.toString());
    });

    // Creating 3 months data array
    timeframeFiltered['3m'] = historicData.slice(historicData.length - 3);

    // Creating 6 months data array
    timeframeFiltered['6m'] = historicData.slice(historicData.length - 6);

    // Creating 1 year data array
    timeframeFiltered['1y'] = historicData.slice(historicData.length - 12);

    const change = timeframeOptions?.reduce((acc, tf) => {
      const value = tf.value
      const tree = historicOptions.tree
      const list = timeframeFiltered[value];

      if (!list || list.length === 0) {
        acc[value] = { sawtimber: 0, pulpwood: 0, total: 0 };
        return acc;
      }

      const firstPrice = list[0];
      const lastPrice = list[list.length - 1];

      const sawtimber = (((lastPrice[tree]?.sawtimber - firstPrice[tree]?.sawtimber) / firstPrice[tree]?.sawtimber) * 100);
      const pulpwood = (((lastPrice[tree]?.pulpwood - firstPrice[tree]?.pulpwood) / firstPrice[tree]?.pulpwood) * 100);

      acc[value] = {
        sawtimber: sawtimber,
        pulpwood: pulpwood,
        total: ((sawtimber + pulpwood) / 2)
      }

      return acc
    }, {});

    return {
      change: change,
      chart: timeframeFiltered[historicOptions.timeframe].map(i => {
        const treeData = i[historicOptions.tree] || {};
        const date = new Date(i.date);

        return {
          label: date.toLocaleString('en-US', { month: 'short', year: '2-digit' }).replace('y', 'j'),
          date: i.date,
          sawtimber: treeData?.sawtimber,
          pulpwood: treeData?.pulpwood,
          all: [
            { value: 'sawtimber', price: treeData?.sawtimber, label: 'Sågtimmer' },
            { value: 'pulpwood', price: treeData?.pulpwood, label: 'Massaved' }
          ]
        }
      })
    }
  }, [historicData, historicOptions])

  const dateFormatter = (value) => {
    const date = new Date(value);
    if (historicOptions.timeframe == '3y' || historicOptions.timeframe == 'max') {
      if (date.getMonth() === 0) {
        return date.toLocaleString('en-US', { month: 'short', year: '2-digit' }).replace('y', 'j');
      } else if (date.getMonth() === 5 && !isMobile) {
        return date.toLocaleString('en-US', { month: 'short', year: '2-digit' }).replace('y', 'j');
      } else {
        return ''
      }
    }

    if (historicOptions.timeframe == 'y' && isMobile) {
      if (!(date.getMonth() % 2)) {
        return date.toLocaleString('en-US', { month: 'short' }).replace('y', 'j');
      } else {
        return ''
      }
    }

    if (historicOptions.timeframe == '1y' && isMobile) {
      if (date.getMonth() % 2) {
        return date.toLocaleString('en-US', { month: 'short' }).replace('y', 'j');
      } else {
        return ''
      }
    }

    return date.toLocaleString('en-US', { month: 'short' }).replace('y', 'j');
  }

  if (!muni) {
    return selectMuni()
  } else if (historicData === null) {
    return <Retry func={() => fetchHistoricPrices()} />
  } else if (!historicData || historicData.length === 0) {
    return <div className='flex flex-col flex-1 p-6'>
      <div className='flex w-full gap-6 overflow-x-auto mb-6'>
        {Array.from({ length: 4 }, (_) => {
          return <Skeleton className="w-[25%] h-[70px]" />
        }
        )}
      </div>
      <div className='flex flex-col flex-1'>
        <Skeleton className='h-full w-full' />
      </div>
    </div>
  } else {
    return (
      <>
        <div className="flex flex-col relative px-6">
          <div className='flex w-full gap-6 overflow-x-auto mb-6' style={{ scrollbarWidth: 'none' }}>
            <TopBlock title='Prisutveckling total' value={data.change[historicOptions.timeframe]?.total} />

            <TopBlock title='Prisutveckling sågtimmer' value={data.change[historicOptions.timeframe]?.sawtimber} />

            <TopBlock title='Prisutveckling massaved' value={data.change[historicOptions.timeframe]?.pulpwood} />

            <TopBlock title='Nuvarande pristrend' value='Sjunkande' trend={false} />
          </div>

          <LineChart
            size={{
              height: 210.6667,
              width: "100%",
            }}
            lineChart={{
              data: data.chart,
              margin: { top: 12, right: 0, bottom: 0, left: -30 },
            }}

            xAxis={{
              dataKey: "date",
              interval: 0,
              tickFormatter: dateFormatter,
              padding: { right: 20, left: 20 }
            }}
            yAxis={{
              tickFormatter: (value) => value.toLocaleString('sv-SE'),
              width: 70
            }}
            dataset={[
              {
                color: "#3F8888",
                dataKey: "pulpwood",
                dot: false
              },
              {
                color: "#7C6ED9",
                dataKey: "sawtimber",
                dot: false
              },
            ]}
            tooltip={<Tooltip />}
          />

          <div className='flex justify-end'>
            {timeframeOptions.map(tf => {
              return <div className={`flex flex-col text-center rounded-sm gap-2 cursor-pointer min-w-[60px] py-4 px-2 mr-1 ${historicOptions.timeframe == tf.value ? 'bg-[#F5F5F5]' : ''}`} onClick={() => setHistoricOptions(prev => ({ ...prev, timeframe: tf.value }))}>
                <Text className={`${historicOptions.timeframe == tf.value ? 'text-[#1A1A1A]' : 'text-[#737373]'}`} variant='text-xs/leading-none/medium'>{tf.label}</Text>
                <BottomValue value={data.change[tf.value]?.total} />
              </div>
            })}
          </div>

          {historicData == 'not_plus_user' && <div className='absolute inset-0 backdrop-blur-[3px] bg-[linear-gradient(to_bottom,rgba(255,255,255,0.2),rgba(255,255,255,0.8))]'>
            <div className='flex flex-col items-center justify-center max-w-[300px] mx-auto h-full'>

              <div className='bg-white filter drop-shadow-[0_0_25px_rgba(97,95,255,0.5)] rounded-xl p-4'>
                <LockIcon fill='#000000' width='25px' height='25px' />
              </div>

              <div className='flex my-4'>
                <PlusIcon fill='#0F0F0F' width='20px' height='20px' />
                <Text className='text-[#0F172B] ml-2' variant='text-base/leading-normal/medium'> Treebula Plus</Text>
              </div>
              <Text className='text-[#1A1A1A]' variant='text-sm/leading-normal/normal'>Följ historisk virkes prisutveckling över tid</Text>
              <Button variant="primary" className='w-full my-3' onClick={() => openPlusBlockerDialog('price-comparison')}>Uppgradera till Plus <ArrowIcon fill='#FBFBFB' width='20px' height='20px' /></Button>
              <Text className='text-[#1A1A1A]' variant='text-xs/leading-normal/normal'>Lås upp analys och insikter</Text>
            </div>
          </div>}
        </div>
      </>
    );
  }
}

